<script lang="ts">
  import type { SurfaceSnapshot, SurfaceProject, SurfaceTask } from "../lib/types";
  import { relativeDate } from "../lib/format";
  import { completeTask } from "../lib/api";
  import { loadSnapshot } from "../lib/store";
  import { optimisticDone, markDone, clearDone, reconcile } from "../lib/optimistic";
  import StarButton from "../lib/StarButton.svelte";
  import SourceBadges from "../lib/SourceBadges.svelte";

  let { data, project, onback }: { data: SurfaceSnapshot; project: SurfaceProject; onback: () => void } = $props();

  let pending = $state<Set<string>>(new Set());
  let lastError = $state<string | null>(null);
  let showCompleted = $state(false);

  function reportError(msg: string) {
    lastError = msg;
  }

  $effect(() => {
    const confirmed = data.tasks.filter((t) => t.state === "done").map((t) => t.fingerprint);
    if (confirmed.length > 0) reconcile(confirmed);
  });

  function groupedTasks() {
    const tasks = data.tasks.filter((t) => t.project_slug === project.slug);
    const groups = new Map<string, SurfaceTask[]>();
    for (const t of tasks) {
      const key = t.is_next_action
        ? "Next Action"
        : t.section ?? "Tasks";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(t);
    }
    const ordered: { name: string; items: SurfaceTask[] }[] = [];
    if (groups.has("Next Action")) ordered.push({ name: "Next Action", items: groups.get("Next Action")! });
    for (const [k, v] of groups) {
      if (k === "Next Action") continue;
      ordered.push({ name: k, items: v });
    }
    return ordered;
  }

  const groups = $derived(groupedTasks());
  const visibleGroups = $derived(
    showCompleted
      ? groups
      : groups
          .map((g) => ({ name: g.name, items: g.items.filter((t) => t.state === "open" && !(t.fingerprint in $optimisticDone)) }))
          .filter((g) => g.items.length > 0),
  );

  const totalOpen = $derived(
    data.tasks.filter((t) => t.project_slug === project.slug && t.state === "open" && !(t.fingerprint in $optimisticDone)).length,
  );
  const totalDone = $derived(
    data.tasks.filter((t) => t.project_slug === project.slug && (t.state === "done" || (t.fingerprint in $optimisticDone))).length,
  );

  async function toggle(t: SurfaceTask) {
    if (pending.has(t.fingerprint)) return;
    lastError = null;
    pending = new Set([...pending, t.fingerprint]);
    markDone(t.fingerprint);
    try {
      await completeTask({
        file: t.file,
        line: t.line,
        text: t.text,
        gt_account: t.account,
        gt_id: t.gt_id,
        target_state: "done",
      });
      void loadSnapshot({ fresh: true });
    } catch (e) {
      lastError = `Couldn't complete: ${e instanceof Error ? e.message : String(e)}`;
      clearDone(t.fingerprint);
    } finally {
      pending = new Set([...pending].filter((fp) => fp !== t.fingerprint));
    }
  }
</script>

<div class="page">
  <button class="back-btn" type="button" onclick={onback} aria-label="Back to projects">
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <path d="M10.7 3.3a1 1 0 0 1 0 1.4L7.4 8l3.3 3.3a1 1 0 1 1-1.4 1.4l-4-4a1 1 0 0 1 0-1.4l4-4a1 1 0 0 1 1.4 0Z" fill="currentColor"/>
    </svg>
    <span>Projects</span>
  </button>

  <header class="hero">
    <div class="title-line">
      {#if project.priority === "high"}<span class="dot priority-high"></span>{/if}
      {#if project.priority === "medium"}<span class="dot priority-medium"></span>{/if}
      <h1>{project.title}</h1>
    </div>
    <div class="meta-line">
      {#if project.domain}<span class="dot {project.domain}"></span><span class="faint">{project.domain}</span><span class="sep">·</span>{/if}
      <span class="faint">{totalOpen} open</span>
      {#if totalDone > 0}<span class="sep">·</span><span class="faint">{totalDone} done</span>{/if}
      {#if project.waiting_count > 0}<span class="sep">·</span><span class="faint">{project.waiting_count} waiting</span>{/if}
      {#if project.due}<span class="sep">·</span><span class="faint">due {relativeDate(project.due)}</span>{/if}
    </div>
  </header>

  {#if lastError}
    <div class="banner error">{lastError}</div>
  {/if}

  {#each visibleGroups as g (g.name)}
    <section>
      <h2 class="section-title">{g.name}</h2>
      <ul class="grouped-list">
        {#each g.items as t (t.fingerprint)}
          <li
            class:pending={pending.has(t.fingerprint)}
            class:done={t.state === "done" || (t.fingerprint in $optimisticDone)}
          >
            <button
              class="check"
              type="button"
              aria-label={t.state === "done" ? "Done" : "Mark done"}
              disabled={pending.has(t.fingerprint) || t.state === "done"}
              onclick={() => toggle(t)}
            >
              <span class="checkbox" class:checked={t.state === "done" || (t.fingerprint in $optimisticDone)}>
                {#if t.state === "done" || (t.fingerprint in $optimisticDone)}
                  <svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true">
                    <path d="M11.7 4.3a1 1 0 0 1 0 1.4l-5 5a1 1 0 0 1-1.4 0l-2.5-2.5a1 1 0 1 1 1.4-1.4L6 8.6l4.3-4.3a1 1 0 0 1 1.4 0Z" fill="white"/>
                  </svg>
                {/if}
              </span>
            </button>
            <span class="text">{t.text}</span>
            <span class="row-end">
              <SourceBadges task={t} />
              {#if t.state !== "done"}
                <StarButton task={t} onerror={reportError} size="sm" />
              {/if}
            </span>
          </li>
        {/each}
      </ul>
    </section>
  {/each}

  {#if project.waiting_on && project.waiting_on.length > 0}
    <section>
      <h2 class="section-title">Waiting On</h2>
      <ul class="grouped-list">
        {#each project.waiting_on as w (w.item + w.since)}
          <li class="waiting-row">
            <div class="waiting-body">
              {#if w.person}<strong>{w.person}</strong> — {/if}{w.item}
            </div>
            <span class="faint">since {relativeDate(w.since)}</span>
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  <div class="footer-actions">
    <label class="show-toggle">
      <input type="checkbox" bind:checked={showCompleted} />
      Show completed
    </label>
  </div>
</div>

<style>
  .page { padding: 8px 16px 140px; max-width: 720px; margin: 0 auto; }

  /* iOS back chevron + label */
  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--accent);
    font-size: 17px;
    font-weight: 400;
    padding: 8px 8px 8px 0;
    margin: 4px 0 0 -4px;
    border-radius: 8px;
  }
  .back-btn:active { opacity: 0.6; }

  .hero { padding: 8px 4px 12px; }
  .title-line { display: flex; align-items: center; gap: 8px; }
  h1 {
    font-size: 28px;
    margin: 0;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .meta-line { font-size: 13px; padding-top: 6px; color: var(--fg-dim); }
  .sep { margin: 0 6px; color: var(--fg-faint); }

  .section-title {
    font-size: 13px;
    margin: 22px 16px 6px;
    color: var(--fg-dim);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 500;
  }
  section { margin-bottom: 4px; }

  .banner.error {
    background: rgba(255, 69, 58, 0.14);
    color: #ff8a82;
    border: 1px solid rgba(255, 69, 58, 0.28);
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    margin-bottom: 12px;
  }

  .grouped-list {
    list-style: none;
    padding: 0;
    margin: 0;
    background: var(--bg-elev);
    border-radius: 12px;
    overflow: hidden;
  }
  .grouped-list li {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    padding: 10px 14px 10px 4px;
    border-bottom: 0.5px solid var(--separator);
    transition: opacity 0.2s;
  }
  .grouped-list li:last-child { border-bottom: 0; }
  .grouped-list li.pending { opacity: 0.5; }
  .grouped-list li.done .text { color: var(--fg-faint); text-decoration: line-through; }

  .waiting-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 12px 14px;
  }
  .waiting-body { font-size: 15px; }

  .check { padding: 6px 10px; border-radius: 8px; flex-shrink: 0; }
  .check:active .checkbox { border-color: var(--accent); }
  .checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1.5px solid var(--fg-faint);
    border-radius: 50%;
    transition: all 0.15s;
  }
  .checkbox.checked { background: var(--accent); border-color: var(--accent); }

  .text {
    flex: 1;
    min-width: 0;
    line-height: 1.32;
    padding-top: 7px;
    font-size: 16px;
    word-wrap: break-word;
  }
  .row-end { display: flex; align-items: center; gap: 6px; flex-shrink: 0; padding-top: 5px; }

  .footer-actions { padding: 24px 4px 0; }
  .show-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--fg-dim);
    font-size: 14px;
    cursor: pointer;
  }
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: var(--accent);
  }
</style>
